import { defineStore } from "pinia";

import referenceItemsApi from "@/services/api/referenceItemsApi";
import { getCompanyId } from "@/utils/userUtils";

export const useReferenceItemsStore = defineStore(
  "referenceItemsStore",
  {
    state: () => ({
      items: [],
      companyId: getCompanyId(),
      isLoading: false,
      error: null,
      targets: [],

      targetModel: "Order",
      targetField: "order_type",

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },

      selectItems: [],
      selectSearchQuery: "",
      selectPagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },
      selectIsLoading: false,
    }),

    actions: {
      async fetchTargets() {
        const response = await referenceItemsApi.targets(this.companyId);
        this.targets = response.data.result || response.data || [];
      },

      setTarget(model, field) {
        this.targetModel = model;
        this.targetField = field;
      },

      async getItemsRequest(params = {}) {
        this.isLoading = true;
        try {
          const response = await referenceItemsApi.index(
            this.companyId,
            {
              "q[target_model]": this.targetModel,
              "q[target_field]": this.targetField,
              ...params,
            }
          );
          this.items = response.data.result || response.data || [];

          const meta = response.data.pagination_meta;
          if (meta) {
            this.pagination = {
              page: meta.page,
              pages: meta.pages,
              series: meta.series,
              next: meta.next,
              prev: meta.prev,
              count: meta.count,
              limit: meta.limit,
            };
          }
        } catch (error) {
          this.error = error;
          console.error(
            "Ошибка при получении элементов справочника:",
            error
          );
        } finally {
          this.isLoading = false;
        }
      },

      async createItem(payload) {
        this.isLoading = true;
        try {
          const response = await referenceItemsApi.create(
            this.companyId,
            {
              target_model: this.targetModel,
              target_field: this.targetField,
              ...payload,
            }
          );
          await this.getItemsRequest();
          return response.data.result || response.data || {};
        } catch (error) {
          console.error(
            "Ошибка при создании элемента справочника:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async updateItem(id, payload) {
        this.isLoading = true;
        try {
          await referenceItemsApi.update(this.companyId, id, payload);
          await this.getItemsRequest();
        } catch (error) {
          console.error(
            "Ошибка при обновлении элемента справочника:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async itemEnable(id) {
        this.isLoading = true;
        try {
          await referenceItemsApi.enable(this.companyId, id);
          await this.getItemsRequest();
        } catch (error) {
          console.error(
            "Ошибка при включении элемента справочника:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async itemDisable(id) {
        this.isLoading = true;
        try {
          await referenceItemsApi.disable(this.companyId, id);
          await this.getItemsRequest();
        } catch (error) {
          console.error(
            "Ошибка при отключении элемента справочника:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async fetchSelectItems(payload = {}) {
        this.selectSearchQuery = payload.search || "";
        return await this.getSelectItemsRequest(
          1,
          payload.targetModel,
          payload.targetField
        );
      },

      async loadMoreSelectItems(targetModel, targetField) {
        if (this.selectIsLoading || !this.selectPagination.next)
          return;
        const nextPage = this.selectPagination.page + 1;
        return await this.getSelectItemsRequest(
          nextPage,
          targetModel,
          targetField
        );
      },

      async getSelectItemsRequest(page, targetModel, targetField) {
        const pageNumber = parseInt(page) || 1;
        this.selectIsLoading = true;
        try {
          const params = {
            page: pageNumber,
            "q[target_model]": targetModel,
            "q[target_field]": targetField,
            "q[enabled]": "true",
          };

          if (this.selectSearchQuery) {
            params["q[search]"] = this.selectSearchQuery;
          }

          const response = await referenceItemsApi.index(
            this.companyId,
            params
          );
          const result = response.data.result || response.data || [];

          if (pageNumber === 1) {
            this.selectItems = result;
          } else {
            this.selectItems = [...this.selectItems, ...result];
          }

          const meta = response.data.pagination_meta;
          if (meta) {
            this.selectPagination = {
              page: meta.page,
              pages: meta.pages,
              series: meta.series,
              next: meta.next,
              prev: meta.prev,
              count: meta.count,
              limit: meta.limit,
            };
          }
        } catch (error) {
          console.error(
            "Ошибка при загрузке элементов для селекта:",
            error
          );
        } finally {
          this.selectIsLoading = false;
        }
      },

      clearSelectItems() {
        this.selectItems = [];
        this.selectSearchQuery = "";
        this.selectPagination = {
          page: 1,
          pages: 1,
          series: [],
          next: null,
          prev: null,
          count: 0,
          limit: 40,
        };
      },
    },
  }
);
